// DNA Pay - Dashboard Financeiro
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, 
  CreditCard, FileText, RefreshCw, Users, Calendar,
  ArrowUpRight, ArrowDownRight, Wallet, PiggyBank,
  BarChart3, PieChart, Activity, Bell, CheckCircle,
  Clock, XCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DnaPayDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [resumo, setResumo] = useState(null);
  const [receitaMensal, setReceitaMensal] = useState([]);
  const [topInadimplentes, setTopInadimplentes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    loadData();
  }, [ano, mes]);

  const loadData = async () => {
    setLoading(true);
    try {
      // V5.2 · Fonte canônica é o default (?engine=legacy força motor antigo)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const [resumoRes, receitaRes, inadimplentesRes, alertasRes, kpisRes, canonicalRes] = await Promise.all([
        fetch(`${API_URL}/api/dnapay/dashboard/resumo?ano=${ano}&mes=${mes}`),
        fetch(`${API_URL}/api/dnapay/dashboard/grafico/receita-mensal?ano=${ano}`),
        fetch(`${API_URL}/api/dnapay/dashboard/top-inadimplentes?limit=5`),
        fetch(`${API_URL}/api/dnapay/dashboard/alertas`),
        fetch(`${API_URL}/api/dnapay/dashboard/kpis?ano=${ano}`),
        useLegacy
          ? Promise.resolve({ ok: false })
          : fetch(`${API_URL}/api/finance/dashboard?year=${ano}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
            }),
      ]);

      if (resumoRes.ok) setResumo(await resumoRes.json());
      if (receitaRes.ok) setReceitaMensal(await receitaRes.json());
      if (inadimplentesRes.ok) setTopInadimplentes(await inadimplentesRes.json());
      if (alertasRes.ok) setAlertas(await alertasRes.json());
      if (kpisRes.ok) setKpis(await kpisRes.json());

      // V5.2 · Sobrescreve os cards principais com números canônicos (única fonte)
      if (canonicalRes.ok) {
        const canon = await canonicalRes.json();
        setResumo((prev) => ({
          ...(prev || {}),
          total_a_receber: canon.a_receber,
          total_recebido: canon.recebido,
          total_vencido: canon.vencido,
          inadimplencia_percentual: canon.inadimplencia_pct,
          titulos_total: canon.titulos?.total,
          titulos_pagos: canon.titulos?.pago,
          titulos_pendentes: canon.titulos?.pendente,
          titulos_vencidos: canon.titulos?.vencido,
          _engine: 'canonical',
        }));
        console.info('[dashboard] usando motor canônico', canon);
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="dnapay-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-8 h-8 text-purple-600" />
            DNA Pay
          </h1>
          <p className="text-gray-500">Sistema Financeiro Completo</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            variant="default"
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => navigate('/v2/financeiro/central-lancamentos')}
            data-testid="dnapay-central-lancamentos-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Todos os Lançamentos
          </Button>
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="space-y-2">
          {alertas.map((alerta, idx) => (
            <div 
              key={idx}
              className={`p-3 rounded-lg flex items-center gap-3 ${
                alerta.tipo === 'danger' ? 'bg-red-50 border border-red-200' :
                alerta.tipo === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-blue-50 border border-blue-200'
              }`}
            >
              <AlertTriangle className={`w-5 h-5 ${
                alerta.tipo === 'danger' ? 'text-red-500' :
                alerta.tipo === 'warning' ? 'text-yellow-500' :
                'text-blue-500'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{alerta.titulo}</p>
                <p className="text-sm text-gray-600">{alerta.mensagem}</p>
              </div>
              <Button variant="ghost" size="sm">{alerta.acao}</Button>
            </div>
          ))}
        </div>
      )}

      {/* Cards principais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-purple-100 text-sm">A Receber (Mês)</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(resumo?.mes_atual?.total_a_receber)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="text-purple-100">{resumo?.mes_atual?.titulos_pendentes || 0} títulos pendentes</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-green-100 text-sm">Recebido (Mês)</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(resumo?.mes_atual?.total_recebido)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <ArrowUpRight className="w-4 h-4" />
              <span>{resumo?.mes_atual?.percentual_recebido || 0}% do previsto</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-red-100 text-sm">Vencido</p>
                <p className="text-2xl font-bold mt-1">
                  {formatCurrency(resumo?.mes_atual?.total_vencido)}
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <TrendingDown className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4" />
              <span>{resumo?.mes_atual?.titulos_vencidos || 0} títulos vencidos</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-blue-100 text-sm">Taxa Inadimplência</p>
                <p className="text-2xl font-bold mt-1">
                  {resumo?.mes_atual?.taxa_inadimplencia || 0}%
                </p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <Activity className="w-6 h-6" />
              </div>
            </div>
            <Progress 
              value={100 - (resumo?.mes_atual?.taxa_inadimplencia || 0)} 
              className="mt-4 h-2 bg-white/20"
            />
          </CardContent>
        </Card>
      </div>

      {/* Ações Rápidas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/v2/financeiro/dnapay/titulos')}
          data-testid="btn-titulos"
        >
          <FileText className="w-6 h-6 text-purple-600" />
          <span>Títulos</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/v2/financeiro/conciliacao')}
          data-testid="btn-conciliacao"
        >
          <RefreshCw className="w-6 h-6 text-green-600" />
          <span>Conciliação</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/v2/financeiro/relatorios')}
          data-testid="btn-relatorios"
        >
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <span>Relatórios</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate('/v2/financeiro/config')}
          data-testid="btn-config"
        >
          <CreditCard className="w-6 h-6 text-orange-600" />
          <span>Gateways</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Receita Mensal */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Receita Mensal {ano}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {receitaMensal.slice(0, mes).map((item) => (
                <div key={item.mes} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{meses[item.mes - 1]}</span>
                    <span className="font-medium">
                      {formatCurrency(item.recebido)} / {formatCurrency(item.previsao)}
                    </span>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={item.previsao > 0 ? (item.recebido / item.previsao * 100) : 0} 
                      className="h-3"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Inadimplentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-red-600" />
              Top Inadimplentes
            </CardTitle>
            <CardDescription>Maiores valores em atraso</CardDescription>
          </CardHeader>
          <CardContent>
            {topInadimplentes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Nenhum inadimplente no momento!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {topInadimplentes.map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => navigate(`/financeiro/titulos?aluno=${item.aluno_id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{item.aluno_nome}</p>
                        <p className="text-xs text-gray-500">{item.turma_nome}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{formatCurrency(item.total_vencido)}</p>
                      <p className="text-xs text-gray-500">{item.quantidade_titulos} título(s)</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPIs do Ano */}
      {kpis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-purple-600" />
              KPIs Financeiros {ano}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-600">Alunos Ativos</p>
                <p className="text-2xl font-bold text-purple-600">{kpis.total_alunos_ativos}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Receita Realizada</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(kpis.receita_realizada_ano)}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Ticket Médio</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(kpis.ticket_medio)}</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-gray-600">Receita/Aluno</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(kpis.receita_por_aluno)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Métodos de Pagamento */}
      {resumo?.por_metodo_pagamento && Object.keys(resumo.por_metodo_pagamento).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Métodos de Pagamento (Mês)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(resumo.por_metodo_pagamento).map(([metodo, valor]) => (
                <div key={metodo} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 capitalize">{metodo.replace('_', ' ')}</p>
                  <p className="text-lg font-bold text-gray-900">{formatCurrency(valor)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
